import { ImportAlertProps } from "components/importer/ImportAlert";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

interface UseAlertsProps {
  discardedRows?: number | undefined;
  successfulImports?: number | undefined;
  entityName: string;
}

const useAlerts = ({
  discardedRows,
  successfulImports,
  entityName,
}: UseAlertsProps): Record<string, ImportAlertProps> => {
  const { t } = useTranslation();

  const alerts: Record<string, ImportAlertProps> = useMemo(() => {
    return {
      WELCOME: {
        severity: "info",
        title: t("alerts.welcome.title", {
          ns: "importWizard",
          entity: entityName,
        }),
        paragraphs: [
          t("alerts.welcome.p1", { ns: "importWizard", entity: entityName }),
        ],
        disableClose: true,
        sx: { mb: 2 },
      },
      INVALID_VALUES: {
        severity: "warning",
        title: t("alerts.invalid_values.title", { ns: "importWizard" }),
        paragraphs: [
          t("alerts.invalid_values.p1", { ns: "importWizard" }),
          t("alerts.invalid_values.p2", { ns: "importWizard" }),
        ],
        sx: { mb: 2 },
      },
      VALIDATION_SUCCESS: {
        severity: "success",
        title: t("alerts.validation_success.title", { ns: "importWizard" }),
        paragraphs: [
          t("alerts.validation_success.p1", { ns: "importWizard" }),
          t("alerts.validation_success.p2", { ns: "importWizard" }),
        ],
        sx: { mb: 2 },
      },
      CSV_IMPORT_TIPS: {
        severity: "warning",
        title: t("alerts.csv_import_tips.title", { ns: "importWizard" }),
        disableClose: true,
        paragraphs: [
          t("alerts.csv_import_tips.p1", { ns: "importWizard" }),
          t("alerts.csv_import_tips.p2", { ns: "importWizard" }),
          t("alerts.csv_import_tips.p3", { ns: "importWizard" }),
          t("alerts.csv_import_tips.p4", { ns: "importWizard" }),
        ],
        sx: { mb: 2 },
      },
      PERFECT_PARSE: {
        severity: "success",
        title: t("alerts.perfect_parse.title", { ns: "importWizard" }),
        paragraphs: [
          t("alerts.perfect_parse.p1", { ns: "importWizard" }),
          t("alerts.perfect_parse.p2", { ns: "importWizard" }),
        ],
        sx: { mb: 2 },
      },
      TEMPLATE_RECOGNIZED: {
        severity: "success",
        title: t("alerts.template_recognized.title", { ns: "importWizard" }),
        paragraphs: [
          t("alerts.template_recognized.p1", { ns: "importWizard" }),
          t("alerts.template_recognized.p2", { ns: "importWizard" }),
        ],
        sx: { mb: 2 },
      },
      MAP_TUTORIAL: {
        severity: "info",
        title: t("alerts.map_tutorial.title", { ns: "importWizard" }),
        paragraphs: [
          t("alerts.map_tutorial.p1", { ns: "importWizard" }),
          t("alerts.map_tutorial.p2", { ns: "importWizard" }),
          t("alerts.map_tutorial.p3", { ns: "importWizard" }),
          t("alerts.map_tutorial.p4", { ns: "importWizard" }),
          t("alerts.map_tutorial.p5", { ns: "importWizard" }),
          t("alerts.map_tutorial.p6", { ns: "importWizard" }),
          t("alerts.map_tutorial.p7", { ns: "importWizard" }),
          t("alerts.map_tutorial.p8", { ns: "importWizard" }),
          t("alerts.map_tutorial.p9", { ns: "importWizard" }),
          t("alerts.map_tutorial.p10", { ns: "importWizard" }),
        ],
        sx: { mb: 2 },
      },
      MAPPING_COMPLETE: {
        severity: "success",
        title: t("alerts.mapping_complete.title", { ns: "importWizard" }),
        paragraphs: [
          t("alerts.mapping_complete.p1", { ns: "importWizard" }),
          t("alerts.mapping_complete.p2", { ns: "importWizard" }),
        ],
        sx: { mb: 2 },
      },
      EXTRACT_VEHICLE_INSTRUCTIONS: {
        severity: "info",
        title: t("alerts.extract_vehicle_instructions.title", {
          ns: "importWizard",
        }),
        disableClose: true,
        paragraphs: [
          t("alerts.extract_vehicle_instructions.p1", { ns: "importWizard" }),
          t("alerts.extract_vehicle_instructions.p2", { ns: "importWizard" }),
          t("alerts.extract_vehicle_instructions.p3", { ns: "importWizard" }),
        ],
      },
      IMPORT_REPORT: {
        title: t("alerts.import_report.title", { ns: "importWizard" }),
        paragraphs: [
          t("alerts.import_report.p1", {
            ns: "importWizard",
            successCount: successfulImports,
          }),
          t("alerts.import_report.p2", { ns: "importWizard" }),
        ],
        severity: "success",
        sx: { mb: 2 },
      },
      DISCARDED_ROWS: {
        title: t("alerts.discarded_rows.title", { ns: "importWizard" }),
        paragraphs: [
          t("alerts.discarded_rows.p1", {
            ns: "importWizard",
            discardedCount: discardedRows,
          }),
        ],
        severity: "warning",
        sx: { mb: 2 },
      },
      DUPLICATE_RECORDS: {
        title: t("alerts.duplicate_records.title", { ns: "importWizard" }),
        paragraphs: [
          t("alerts.duplicate_records.p1", {
            ns: "importWizard",
            discardedCount: discardedRows,
          }),
        ],
        severity: "warning",
        sx: { mb: 2 },
      },
    };
  }, [discardedRows, successfulImports, entityName, t]);

  return alerts;
};

export default useAlerts;
